import {
  getLegacyLaminaries,
  getLegacyLaminarySections,
  getLaminariaSectionDetail,
} from '../../models/laminariaImages.js';
import { getVimeoItems } from '../../models/videos.js';
import { getSketchfabCollections, getModelsFromCollection } from '../../api/sketchfab.js';

/**
 * Registro de adaptadores por tipo de recurso.
 *
 * Cada adaptador expone `getLevel(pathSegments)` que, dada la profundidad actual
 * (el rastro de ids desde la raíz), devuelve `{ title, items }`. Cada item se
 * normaliza a una forma común:
 *
 *   { id, title, image?, icon, kind: 'container' | 'leaf', kindLabel, ctaLabel }
 *
 * - `container`: al hacer click se profundiza en otra lista (mismo ResourcesList).
 * - `leaf`: al hacer click se abre el detalle (ResourcesDetail, pendiente).
 *
 * Toda la diferencia entre recursos (forma de datos, endpoints, profundidad)
 * vive aquí; ResourcesList y ResourceCard permanecen genéricos.
 */

const pickVimeoThumbnail = video => {
  const sizes = video?.pictures?.sizes || [];
  if (!sizes.length) {
    return null;
  }
  const index = Math.min(3, sizes.length - 1);
  return sizes[index]?.link || sizes[sizes.length - 1]?.link || null;
};

const laminaria = {
  label: 'Laminario de Imágenes',
  getLevel: async segments => {
    // Nivel 0: laminarias
    if (segments.length === 0) {
      const response = await getLegacyLaminaries();
      return {
        title: 'Laminario de Imágenes',
        items: (response || []).map(item => ({
          id: item.id,
          title: item.name,
          image: item.imageURL,
          icon: 'laminaria',
          kind: 'container',
          kindLabel: 'Laminaria',
          ctaLabel: 'Ver secciones',
        })),
      };
    }

    // Nivel 1: secciones de una laminaria
    if (segments.length === 1) {
      const response = await getLegacyLaminarySections(segments[0]);
      return {
        title: response?.name || 'Laminaria',
        items: (response?.sections || []).map(section => ({
          id: section.id,
          title: section.name,
          image: section.imageURL,
          icon: 'section',
          kind: 'container',
          kindLabel: 'Sección',
          ctaLabel: 'Ver imágenes',
        })),
      };
    }

    // Nivel 2: imágenes de una sección (hojas -> detalle)
    const section = await getLaminariaSectionDetail(segments[0], segments[1]);
    return {
      title: section?.name || 'Sección',
      items: (section?.images || []).map((image, index) => ({
        id: image.image_id ?? index,
        title: image.title,
        image: image.imageURL,
        icon: 'image',
        kind: 'leaf',
        kindLabel: 'Imagen',
        ctaLabel: 'Ver detalles',
      })),
    };
  },
};

const videos = {
  label: 'Videos Educativos',
  getLevel: async segments => {
    // La raíz no lleva id; los niveles siguientes usan el id de la carpeta actual.
    const folderId = segments.length ? parseInt(segments[segments.length - 1], 10) : undefined;
    const response = await getVimeoItems(folderId);
    const items = (response?.data || []).map((item, index) => {
      if (item.type === 'folder') {
        const id = item.folder.uri.split('/')[4];
        return {
          id,
          title: item.folder.name,
          icon: 'folder',
          kind: 'container',
          kindLabel: 'Carpeta',
          ctaLabel: 'Ver carpeta',
        };
      }
      const id = item.video.uri.split('/')[2];
      return {
        id,
        title: item.video.name,
        image: pickVimeoThumbnail(item.video),
        icon: 'video',
        kind: 'leaf',
        kindLabel: 'Video',
        ctaLabel: 'Ver video',
      };
    });
    return { title: 'Videos Educativos', items };
  },
};

const anatomicalCollections = {
  label: 'Estructuras Anatómicas 3D',
  getLevel: async segments => {
    // Nivel 0: colecciones
    if (segments.length === 0) {
      const response = await getSketchfabCollections();
      return {
        title: 'Estructuras Anatómicas 3D',
        items: (response || []).map(collection => ({
          id: collection.uid,
          title: collection.name,
          icon: 'collection',
          kind: 'container',
          kindLabel: 'Colección',
          ctaLabel: 'Ver estructuras',
        })),
      };
    }

    // Nivel 1: estructuras de una colección (hojas -> detalle)
    const structures = await getModelsFromCollection(segments[0]);
    return {
      title: 'Estructuras',
      items: (structures || []).map(structure => ({
        id: structure.uid,
        title: structure.name,
        image: structure.uri,
        icon: 'structure',
        kind: 'leaf',
        kindLabel: 'Estructura',
        ctaLabel: 'Ver estructura',
      })),
    };
  },
};

export const resourcesConfig = {
  laminaria,
  videos,
  'anatomical-collections': anatomicalCollections,
};

export default resourcesConfig;
